import { Component, OnInit, OnDestroy, HostListener, ChangeDetectorRef } from '@angular/core';
import { UrlConstants } from '../../../shared/utils/url-constants';
import { PageDataService } from '../../services/page-data.service';
import { NotificationApiService } from '../../services/notification-api.service';
import { AuthService } from '../../services/auth.service';
import { NotificationWebSocketService } from '../../services/notification-websocket.service';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ChangePasswordModalComponent } from '../../../modules/account/dialogs/change-password-modal/change-password-modal.component';
import { AppConstants } from '../../../shared/utils/app-constants';
import { AppConfigService } from '../../services/app-config.service';
import { ApprovalCountService } from '../../services/approval-count.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from '../../services/notifications.service';
 
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls:['./header.component.scss']
 
})
export class HeaderComponent implements OnInit, OnDestroy {
  UrlConstants = UrlConstants;
  unreadCount = 0;
  approvalCount = 0;
  showNotifications: boolean = false;
  showUserDropdown: boolean = false;
  unreadNotifications: any[] = [];
  isMarkingAllAsRead = false;
 
  // WebSocket related
  isNotificationWSConnected = false;
  private notificationSubscription?: Subscription;
  private wsConnectionSubscription?: Subscription;
  private approvalCountSubscription?: Subscription;
  private userUpdateSubscription?: Subscription;
  private isNotificationInitialized = false;
 
  constructor(
    public pageDataService: PageDataService,
    private notificationApiService: NotificationApiService,
    public authService: AuthService,
    private notificationWebSocketService: NotificationWebSocketService,
    private changeDetectorRef: ChangeDetectorRef,
    private matDialog: MatDialog,
    private router:Router,
    private approvalCountService: ApprovalCountService,
    private translateService: TranslateService,
    private notificationsService: NotificationsService
  ) {}
 
  ngOnInit() {
    const hostElement = this.getHostElement();
    if (hostElement) {
      hostElement.classList.add('visible');
    }   
    // Subscribe to user updates to initialize notifications when ready
    this.userUpdateSubscription = this.authService.userUpdated.subscribe(() => {
      this.checkAndInitializeNotifications();
    });

    // Also check immediately in case user is already loaded
    this.checkAndInitializeNotifications();
    this.approvalCountSubscription = this.approvalCountService.totalCount$.subscribe(count => {
      this.approvalCount = count;
      this.changeDetectorRef.detectChanges();
    });
  }
 
  ngOnDestroy() {
    this.notificationWebSocketService.disconnect();
    this.notificationSubscription?.unsubscribe();
    this.wsConnectionSubscription?.unsubscribe();
    this.userUpdateSubscription?.unsubscribe();
  }
 
  /**
   * Check if user should initialize notification system
   */
  private checkAndInitializeNotifications(): void {
    // Connect if user is standard (for notifications) OR if they can view approvals (for approval signals)
    if (this.isStandardUser() || this.canViewApprovals()) {
      this.initializeNotificationSystem();
    }
  }

  /**
   * Initialize notification system: fetch data and connect WebSocket
   */
  private initializeNotificationSystem(): void {
    // Connect to WebSocket
    this.notificationWebSocketService.connect();

    // Fetch initial notification data only for standard users
    if (this.isStandardUser()) {
      this.fetchUnreadNotificationCount();
      this.fetchUnreadNotifications();
    }
    
    // Subscribe to connection status
    this.wsConnectionSubscription?.unsubscribe();
    this.wsConnectionSubscription = this.notificationWebSocketService.isConnected$.subscribe(
      (connected) => {
        this.isNotificationWSConnected = connected;
        this.changeDetectorRef.detectChanges();
      }
    );

    // Subscribe to new real-time notifications
    this.notificationSubscription?.unsubscribe();
    this.notificationSubscription = this.notificationWebSocketService.notifications$.subscribe(
      (notification) => {
        this.handleNewNotification(notification);
      }
    );
  }
 
 
  /**
   * Trigger bell animation when new notification arrives
   */
  private triggerNotificationAnimation(): void {
    const bellElement = document.querySelector('.notification-bell');
    if (bellElement) {
      bellElement.classList.add('new-notification');
      setTimeout(() => {
        bellElement.classList.remove('new-notification');
      }, 1000);
    }
  }
 
  /**
   * Play notification sound
   */
  private playNotificationSound(): void {
    try {
      const audio = new Audio('assets/sounds/notification.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {});
    } catch (error) {
      }
  }
 
  /**
   * Show browser notification
   */
  private showBrowserNotification(notification: any): void {
    if (!('Notification' in window)) {
      return;
    }
 
    if (Notification.permission === 'granted') {
      new Notification(notification.title || 'New Notification', {
        body: notification.message,
        icon: 'assets/img/wl-full-logo-white-new.png',
        badge: 'assets/img/wl-full-logo-white-new.png'
      });
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification(notification.title || 'New Notification', {
            body: notification.message,
            icon: 'assets/img/wl-full-logo-white-new.png'
          });
        }
      });
    }
  }
 
  private getHostElement(): HTMLElement | null {
    return document.querySelector('app-header');
  }
 
  /**
   * Close dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    
    // Notification dropdown handling
    const notificationContainer = target.closest('.notification-dropdown, .notification-icon, .notification-wrapper');
    if (!notificationContainer && this.showNotifications) {
      this.closeNotifications();
    }

    // User dropdown handling
    const userContainer = target.closest('.user-profile-wrapper');
    if (!userContainer && this.showUserDropdown) {
      this.showUserDropdown = false;
    }
  }
 
  toggleSidenav() {
    this.pageDataService.toggleSidenav();
  }

  canViewApprovals(): boolean {
    return this.authService.isSuperAdmin() || this.authService.isHelpDesk();
  }

  canViewTaxLevels(): boolean {
    return true; // Usually visible to most authenticated users
  }

  canViewConfiguration(): boolean {
    return this.authService.isIssuerAdminOrSuperAdmin() && (AppConfigService.settings?.settings?.enabled || false);
  }

  /**
   * Toggle notification dropdown
   */
  onNotificationClick() {
    this.showNotifications = !this.showNotifications;
    this.showUserDropdown = false;
   
    // Fetch latest notifications if opening dropdown and list is empty
    if (this.showNotifications && this.unreadNotifications.length === 0) {
       this.fetchUnreadNotifications();
    }
  }
 
  closeNotifications() {
    this.showNotifications = false;
  }

  toggleUserDropdown() {
    this.showUserDropdown = !this.showUserDropdown;
    this.showNotifications = false;
  }

  getInitials(): string {
    const firstName = this.authService.user?.First_Name || '';
    const lastName = this.authService.user?.Last_Name || '';
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  }

  openChangePassword() {
    this.showUserDropdown = false;
    this.matDialog.open(ChangePasswordModalComponent, AppConstants.matDialogConfig());
  }

  openUserProfile() {
    this.showUserDropdown = false;
    this.router.navigate([UrlConstants.ACCOUNT]);
  }

  logout() {
    this.authService.logout();
  }
 
  /**
   * Fetch unread notification count from API
   */
  fetchUnreadNotificationCount(): void {
    this.notificationApiService.getUnreadNotificationCount().subscribe({
      next: (count) => {
        this.unreadCount = count;
      },
      error: (error) => {
        console.error('Failed to fetch notification count:', error);
      }
    });
  }
 
  /**
   * Fetch unread notifications from API
   */
  fetchUnreadNotifications(): void {
    this.notificationApiService.getUnreadNotifications().subscribe({
      next: (notifications) => {
        this.unreadNotifications = notifications;
       },
      error: (error) => {
        console.error('Failed to fetch notifications:', error);
      }
    });
  }
 
  /**
   * Mark single notification as read
   */
  markAsRead(notification: any): void {
    if (!notification.id) {
      console.error('Notification ID is missing');
      return;
    }
   notification.isMarking = true;
 
    this.notificationApiService.markNotificationAsRead(notification.id).subscribe({
      next: () => {
        // Remove from unread list
        this.unreadNotifications = this.unreadNotifications.filter(n => n.id !== notification.id);
       
        // Update the unread count
        this.unreadCount = Math.max(0, this.unreadCount - 1);
       
        // Close dropdown if no more notifications
        if (this.unreadNotifications.length === 0) {
          setTimeout(() => {
            this.showNotifications = false;
          }, 500);
        }
      },
      error: (error) => {
        console.error('Failed to mark notification as read:', error);
        notification.isMarking = false;
       
        this.notificationsService.error('Error', 'Failed to mark notification as read. Please try again.', 5000);
      }
    });
  }
 
  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    if (this.unreadNotifications.length === 0) {
      return;
    }
   this.isMarkingAllAsRead = true;
 
    this.notificationApiService.markAllNotificationsAsRead().subscribe({
      next: () => {
        // Clear all notifications
        this.unreadNotifications = [];
        this.unreadCount = 0;
        this.isMarkingAllAsRead = false;
       
        // Close the dropdown after a short delay
        setTimeout(() => {
          this.showNotifications = false;
        }, 500);
      },
      error: (error) => {
        console.error('Failed to mark all notifications as read:', error);
        this.isMarkingAllAsRead = false;
       
        this.notificationsService.error('Error', 'Failed to mark all notifications as read. Please try again.', 5000);
      }
    });
  }
  /**
 * Format notification timestamp to relative time
 * Converts UTC timestamp from backend to browser's local timezone
 */
formatNotificationTime(timestamp: string): string {
  if (!timestamp) return '';
  
  try {
    // Backend sends UTC time without 'Z' suffix: "2025-10-14T11:34:20.339819"
    // Add 'Z' to ensure it's parsed as UTC
    const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    const date = new Date(utcTimestamp);
    
    // Validate the date
    if (isNaN(date.getTime())) {
      console.error('Invalid timestamp format:', timestamp);
      return 'Invalid date';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    
    // Handle future timestamps (clock sync issues)
    if (diffMs < 0) {
      console.warn('Future timestamp detected:', timestamp);
      return 'Just now';
    }
    
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    
    // Return relative time strings
    if (diffSecs < 10) return 'Just now';
    if (diffSecs < 60) return `${diffSecs}s ago`;
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffWeeks < 4) return `${diffWeeks}w ago`;
    if (diffMonths < 12) return `${diffMonths}mo ago`;
    
    // For older notifications, show formatted date in local timezone
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting notification time:', error, timestamp);
    return '';
  }
}

/**
 * Get full formatted date and time in local timezone
 * Useful for tooltips or detailed views
 */
formatNotificationFullTime(timestamp: string): string {
  if (!timestamp) return '';
  
  try {
    const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    const date = new Date(utcTimestamp);
    
    if (isNaN(date.getTime())) return 'Invalid date';
    
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting full time:', error);
    return '';
  }
}

/**
 * Get browser timezone name
 */
getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Debug method to verify timezone conversion
 * Can be removed in production
 */
private debugTimezoneConversion(timestamp: string): void {
  if (!timestamp) return;
  
  const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
  const date = new Date(utcTimestamp);
  
  console.group('🕐 Timezone Conversion Debug');
  console.groupEnd();
}

/**
 * Handle new real-time notification with latency tracking
 */
private handleNewNotification(notification: any): void {
  const receiveTime = Date.now();
   // Debug timezone conversion (uncomment for debugging)
  // this.debugTimezoneConversion(notification.createdAt);
  
  // Calculate latency with proper UTC handling
  if (notification.createdAt) {
    try {
      const utcTimestamp = notification.createdAt.endsWith('Z') 
        ? notification.createdAt 
        : notification.createdAt + 'Z';
      const createTime = new Date(utcTimestamp).getTime();
      const latency = receiveTime - createTime;
      
       // Warn if latency is unusually high
      if (latency > 5000) {
        console.warn('⚠️ High latency detected:', latency, 'ms');
      }
      
      // Warn if latency is negative (clock sync issue)
      if (latency < 0) {
        console.warn('⚠️ Negative latency - possible clock sync issue');
      }
    } catch (error) {
      console.warn('Could not calculate latency:', error);
    }
  }

  // Check if notification already exists (avoid duplicates)
  const exists = this.unreadNotifications.some(n => n.id === notification.id);
  
  if (!exists) {
    // Add to beginning of array for newest-first display
    this.unreadNotifications.unshift(notification);
    this.unreadCount++;
    
    // Trigger UI animations and notifications
    this.triggerNotificationAnimation();
    this.playNotificationSound();
    this.showBrowserNotification(notification);
    
    // Force Angular change detection to update UI immediately
    this.changeDetectorRef.detectChanges();
  } 
}
 
  /**
   * Check if current user is a standard user (not admin/readonly/helpdesk)
   */
  isStandardUser(): boolean {
    try {
      const isSuperAdmin = this.authService.isSuperAdmin();
      const isReadOnly = this.authService.isReadOnly();
      const isHelpDesk = this.authService.isHelpDesk();
      const isStandardUser = this.authService.isStandardUser();
      
      return isStandardUser; // Add return statement
    } catch (error) {
      console.error('Error checking user type:', error);
      return false; // Return default value on error
    }
  }

  /**
   * Translates notification titles and messages dynamically to French/Spanish when active.
   * Handles regex-based matching to extract variable entity names.
   */
  translateNotificationText(text: string): string {
    if (!text) return '';
    
    const currentLang = this.translateService.currentLang || 'en';
    if (currentLang !== 'fr' && currentLang !== 'fr_fr' && currentLang !== 'es' && currentLang !== 'es_es') {
      return this.translateService.instant(text) || text;
    }

    if (currentLang === 'fr' || currentLang === 'fr_fr') {
      // Static matches
      if (text === 'Facility Modification Pending Approval') {
        return "Modification de l'établissement en attente d'approbation";
      }
      if (text === 'Facility Approval Completed') {
        return "Approbation de l'établissement terminée";
      }

      // 1. Facility approved
      // Your Facility "UBL_Facility" modifications are approved by the authorities.
      const facilityModMatch = text.match(/^Your Facility "([^"]+)" modifications are approved by the authorities\.$/);
      if (facilityModMatch) {
        return `Les modifications de votre établissement "${facilityModMatch[1]}" ont été approuvées par les autorités.`;
      }

      // 2. EO approved
      // Your Economic Operator "United Breweries Limited" has been approved by the authorities.
      const eoApprovedMatch = text.match(/^Your Economic Operator "([^"]+)" has been approved by the authorities\.$/);
      if (eoApprovedMatch) {
        return `Votre opérateur économique "${eoApprovedMatch[1]}" a été approuvé par les autorités.`;
      }

      // 3. EO modifications approved
      // Your Economic Operator "United Breweries Limited" modifications are approved by the authorities.
      const eoModMatch = text.match(/^Your Economic Operator "([^"]+)" modifications are approved by the authorities\.$/);
      if (eoModMatch) {
        return `Les modifications de votre opérateur économique "${eoModMatch[1]}" ont été approuvées par les autorités.`;
      }
    }

    if (currentLang === 'es' || currentLang === 'es_es') {
      // Static matches
      if (text === 'Facility Modification Pending Approval') {
        return "Modificación de la instalación pendiente de aprobación";
      }
      if (text === 'Facility Approval Completed') {
        return "Aprobación de la instalación completada";
      }

      // 1. Facility approved
      const facilityModMatch = text.match(/^Your Facility "([^"]+)" modifications are approved by the authorities\.$/);
      if (facilityModMatch) {
        return `Las modificaciones de su instalación "${facilityModMatch[1]}" han sido aprobadas por las autoridades.`;
      }

      // 2. EO approved
      const eoApprovedMatch = text.match(/^Your Economic Operator "([^"]+)" has been approved by the authorities\.$/);
      if (eoApprovedMatch) {
        return `Su operador económico "${eoApprovedMatch[1]}" ha sido aprobado por las autoridades.`;
      }

      // 3. EO modifications approved
      const eoModMatch = text.match(/^Your Economic Operator "([^"]+)" modifications are approved by the authorities\.$/);
      if (eoModMatch) {
        return `Las modificaciones de su operador económico "${eoModMatch[1]}" han sido aprobadas por las autoridades.`;
      }
    }

    return this.translateService.instant(text) || text;
  }
 
}